import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { CheckCircle, Cancel, Download } from '@mui/icons-material';
import { apiService } from '../services/api';
import { resolveDownloadFileName } from '../utils/downloadFileName';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../utils/sharePointLinkUtils';
import {
  dismissAfterActionForm,
  EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE,
  shouldNavigateIntoPortfolioAfterAction,
} from '../utils/actionFormNavigation';
import { DocumentComments } from '../components/ProjectExecution/DocumentComments';
import type { DocumentAssociation, ApprovalContext } from '../types';

type ApprovalAction = 'approve' | 'reject';

const ApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submittedAction, setSubmittedAction] = useState<ApprovalAction | null>(null);
  const [emailRequired, setEmailRequired] = useState(true);
  const [actionDocument, setActionDocument] = useState<DocumentAssociation | null>(null);
  const [approvalContext, setApprovalContext] = useState<ApprovalContext | null>(null);

  const token = searchParams.get('token');
  const actionIdParam = searchParams.get('actionId');
  const projectIdParam = searchParams.get('projectId');
  const actionId = actionIdParam ? parseInt(actionIdParam, 10) : null;
  const projectId = projectIdParam ? parseInt(projectIdParam, 10) : null;

  const effectiveSpItemId = approvalContext?.spItemId ?? actionDocument?.spItemId ?? null;

  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
  }, [user?.email, email]);

  useEffect(() => {
    if (!token && !actionId) {
      setError('Invalid approval link. Token or actionId is missing.');
      setEmailRequired(false);
    } else if (!token && actionId) {
      if (user?.email) {
        setEmail(user.email);
      }
      setEmailRequired(false);
      if (!isAuthenticated) {
        setError('Authentication required. Please log in to approve this document.');
        setEmailRequired(false);
      }
    }
  }, [token, actionId, user, isAuthenticated]);

  useEffect(() => {
    if (!token && !actionId) {
      setApprovalContext(null);
      return;
    }
    let cancelled = false;
    const params = token ? { token } : { actionId: actionId! };
    apiService.getApprovalContext(params).then((ctx) => {
      if (!cancelled) setApprovalContext(ctx);
    }).catch((err) => {
      if (!cancelled) console.error('Error loading approval context:', err);
    });
    return () => { cancelled = true; };
  }, [token, actionId]);

  useEffect(() => {
    if (!actionId) {
      setActionDocument(null);
      return;
    }
    let cancelled = false;
    apiService.getActionDocuments(actionId).then((docs) => {
      if (!cancelled && docs?.length > 0) {
        setActionDocument(docs[0]);
      }
    }).catch((err) => {
      if (!cancelled) console.error('Error loading action documents:', err);
    });
    return () => { cancelled = true; };
  }, [actionId]);

  const handleSubmit = async (action: ApprovalAction) => {
    const emailToUse = (email || user?.email || '').trim();

    if (emailRequired) {
      if (!emailToUse) {
        setError('Please enter your email address');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailToUse)) {
        setError('Please enter a valid email address');
        return;
      }
    } else {
      if (!emailToUse) {
        setError('Email address is required. Please ensure you are logged in.');
        return;
      }
    }

    if (!token && !actionId) {
      setError('Invalid approval link. No token or actionId provided.');
      return;
    }

    if (!token && actionId && !isAuthenticated) {
      setError('Authentication required. Please log in and try again.');
      return;
    }

    if (action === 'reject' && (!comment || comment.trim() === '')) {
      setError('Comment is required when rejecting an approval.');
      return;
    }

    setError(null);
    setLoading(true);
    setEmail(emailToUse);

    try {
      if (action === 'approve') {
        await apiService.approveAction(token || null, emailToUse, actionId, comment.trim() || undefined);
        setSubmittedAction('approve');
        setSuccess(true);
        const redirectProjectId = projectId ?? approvalContext?.projectId ?? null;
        if (redirectProjectId && shouldNavigateIntoPortfolioAfterAction(user)) {
          setTimeout(() => {
            navigate(`/execution?projectId=${redirectProjectId}`);
          }, 2000);
        }
      } else {
        const rejectResponse = await apiService.rejectAction(token || null, emailToUse, actionId, comment.trim() || undefined);
        setSubmittedAction('reject');
        setSuccess(true);
        const redirectProjectId = rejectResponse?.projectId ?? projectId ?? approvalContext?.projectId ?? null;
        if (redirectProjectId && shouldNavigateIntoPortfolioAfterAction(user)) {
          setTimeout(() => {
            navigate(`/execution?projectId=${redirectProjectId}&refresh=1`, { replace: true });
          }, 1500);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} document`);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {submittedAction === 'approve' ? (
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          ) : (
            <Cancel sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          )}
          <Typography variant="h5" gutterBottom>
            Document {submittedAction === 'approve' ? 'Approved' : 'Rejected'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you, {email}. Your {submittedAction === 'approve' ? 'approval' : 'rejection'} has been recorded.
          </Typography>
          {effectiveSpItemId != null && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <DocumentComments
                file={{
                  spItemId: effectiveSpItemId,
                  siteId: '',
                  driveId: '',
                  itemId: '',
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={async () => {
                  try {
                    const nameOpts = {
                      fileName: approvalContext?.fileName ?? actionDocument?.fileName,
                      documentTitle: approvalContext?.documentTitle ?? actionDocument?.documentTitle,
                      sourceDocumentTitle: approvalContext?.sourceDocumentTitle ?? undefined,
                    };
                    if (isInternalUser(user)) {
                      const { blob, fileName } = await apiService.viewFileWithInfo(
                        effectiveSpItemId,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    } else {
                      const { blob, fileName } = await apiService.downloadFileWithInfo(
                        effectiveSpItemId,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    }
                  } catch (err) {
                    console.error('Failed to open document:', err);
                  }
                }}
              >
                {isInternalUser(user) ? 'View document' : 'Download document'}
              </Button>
            </Box>
          )}
          {!shouldNavigateIntoPortfolioAfterAction(user) ? (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE}
            </Typography>
          ) : (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => {
                const pid = projectId ?? approvalContext?.projectId ?? null;
                dismissAfterActionForm(navigate, user, {
                  internalProjectId: pid,
                  internalFallbackPath: '/',
                });
              }}
            >
              {(projectId ?? approvalContext?.projectId) ? 'Return to Execution View' : 'Return to Home'}
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckCircle sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Document Approval
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review the document details below, then approve or reject.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {(approvalContext && (approvalContext.projectName || approvalContext.phaseName || approvalContext.phaseDescription || approvalContext.taskName || approvalContext.taskDescription || approvalContext.approvalActionName || approvalContext.sourceActionName || approvalContext.documentTitle || approvalContext.fileName)) && (
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              p: 2,
              textAlign: 'left',
              borderLeft: '4px solid',
              borderColor: 'warning.main',
              backgroundColor: 'warning.light',
              '& .MuiTypography-root': { fontSize: '0.95rem' },
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} color="warning.dark" gutterBottom>
              Approval Request
            </Typography>
            {approvalContext.projectName && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Project:</strong> {approvalContext.projectName}
              </Typography>
            )}
            {(approvalContext.phaseName || approvalContext.phaseDescription) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Phase:</strong> {approvalContext.phaseName || ''}
                {approvalContext.phaseDescription ? ` — ${approvalContext.phaseDescription}` : ''}
              </Typography>
            )}
            {(approvalContext.taskName || approvalContext.taskDescription) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Task:</strong> {approvalContext.taskName || ''}
                {approvalContext.taskDescription ? ` — ${approvalContext.taskDescription}` : ''}
              </Typography>
            )}
            {(approvalContext.approvalActionName || approvalContext.approvalActionDescription) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Approval action:</strong> {approvalContext.approvalActionName || ''}
                {approvalContext.approvalActionDescription ? ` — ${approvalContext.approvalActionDescription}` : ''}
              </Typography>
            )}
            {(approvalContext.sourceActionName || approvalContext.sourceDocumentTitle || approvalContext.sourceDocumentDescription) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Source action / document:</strong> {approvalContext.sourceActionName || ''}
                {approvalContext.sourceDocumentTitle ? ` — ${approvalContext.sourceDocumentTitle}` : ''}
                {approvalContext.sourceDocumentDescription ? ` / ${approvalContext.sourceDocumentDescription}` : ''}
              </Typography>
            )}
            {(approvalContext.documentTitle || approvalContext.documentDescription) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Document:</strong> {approvalContext.documentTitle || ''}
                {approvalContext.documentDescription ? (approvalContext.documentTitle ? ` / ${approvalContext.documentDescription}` : approvalContext.documentDescription) : ''}
              </Typography>
            )}
            {approvalContext.fileName && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>File:</strong> {approvalContext.fileName}
              </Typography>
            )}
            {effectiveSpItemId != null && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                sx={{ mt: 1 }}
                onClick={async () => {
                  try {
                    const nameOpts = {
                      fileName: approvalContext?.fileName ?? actionDocument?.fileName,
                      documentTitle: approvalContext?.documentTitle ?? actionDocument?.documentTitle,
                      sourceDocumentTitle: approvalContext?.sourceDocumentTitle ?? undefined,
                    };
                    if (isInternalUser(user)) {
                      const { blob, fileName } = await apiService.viewFileWithInfo(
                        effectiveSpItemId,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    } else {
                      const { blob, fileName } = await apiService.downloadFileWithInfo(
                        effectiveSpItemId,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    }
                  } catch (err) {
                    console.error('Failed to open document:', err);
                  }
                }}
              >
                {isInternalUser(user) ? 'View document' : 'Download document'}
              </Button>
            )}
          </Paper>
        )}

        {(effectiveSpItemId != null) && (
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <DocumentComments
              file={{
                spItemId: effectiveSpItemId,
                siteId: '',
                driveId: '',
                itemId: '',
              }}
            />
          </Box>
        )}

        {(emailRequired || (!token && actionId)) && (
          <Box sx={{ mb: 3 }}>
            {emailRequired && !email && !user?.email ? (
              <TextField
                fullWidth
                label="Your Email Address"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                disabled={Boolean(loading || (!token && !actionId) || (!token && actionId && !isAuthenticated))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !loading && (token || actionId)) {
                    handleSubmit('approve');
                  }
                }}
                sx={{ mb: 2 }}
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Your Email Address
                </Typography>
                <Typography variant="body1" component="span">
                  {email || user?.email || '—'}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Comment (required for rejection)"
              variant="outlined"
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setError(null);
              }}
              disabled={loading}
              placeholder="Add a comment with your approval or rejection"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={() => handleSubmit('approve')}
                disabled={loading || (!token && !actionId) || (!email && !user?.email)}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {loading ? 'Processing...' : 'Approve'}
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                onClick={() => handleSubmit('reject')}
                disabled={loading || (!token && !actionId) || (!email && !user?.email) || !comment.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <Cancel />}
              >
                {loading ? 'Processing...' : 'Reject'}
              </Button>
            </Box>
          </Box>
        )}

        {!token && !actionId && (
          <Alert severity="error">
            Invalid approval link. Token or actionId is missing. Please check the link and try again.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ApprovalPage;
